import React, { useState } from 'react';
import { 
  AlertCircle, 
  Upload, 
  Loader2, 
  CheckCircle,
  Headphones,
  MessageSquare,
  FileText,
  CreditCard,
  Package,
  Settings
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedInput,
  BrandedSelect,
  BrandedTextarea,
  BrandedBadge,
  BrandedAlert
} from '../../components/branding/BrandedComponents';

interface CustomerTicketFormProps {
  companyName?: string;
  onTicketSubmitted?: (ticketId: string) => void;
}

type TicketCategory = 'technical' | 'billing' | 'product' | 'general';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

interface TicketFormData {
  customerName: string;
  email: string;
  category: TicketCategory;
  priority: TicketPriority;
  subject: string;
  description: string;
  attachments?: File[];
}

export const CustomerTicketForm: React.FC<CustomerTicketFormProps> = ({
  companyName = 'Support Center',
  onTicketSubmitted
}) => {
  return (
    <BrandingProvider templateType="business" initialBranding={{ companyName }}>
      <CustomerTicketFormContent 
        companyName={companyName}
        onTicketSubmitted={onTicketSubmitted}
      />
    </BrandingProvider>
  );
};

const CustomerTicketFormContent: React.FC<CustomerTicketFormProps> = ({
  companyName = 'Support Center',
  onTicketSubmitted
}) => {
  const [formData, setFormData] = useState<TicketFormData>({
    customerName: '',
    email: '',
    category: 'general',
    priority: 'medium',
    subject: '',
    description: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);

  const categoryOptions = [
    { value: 'technical', label: 'Technical Issue', icon: Settings },
    { value: 'billing', label: 'Billing & Payments', icon: CreditCard },
    { value: 'product', label: 'Product Question', icon: Package },
    { value: 'general', label: 'General Inquiry', icon: MessageSquare }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low - Can wait a few days' },
    { value: 'medium', label: 'Medium - Need help soon' },
    { value: 'high', label: 'High - Blocking my work' },
    { value: 'urgent', label: 'Urgent - Critical issue' }
  ];

  const handleInputChange = (field: keyof TicketFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Extract organization ID from current URL path
      // URL format: /org_1754553059623_u8zwgg/templates/support/customer
      const currentPath = window.location.pathname;
      console.log('Current path:', currentPath);
      const orgMatch = currentPath.match(/\/(org_[^\/]+)\//);
      const organizationId = orgMatch ? orgMatch[1] : null;
      console.log('Extracted organization ID:', organizationId);

      if (!organizationId) {
        throw new Error('Unable to determine organization from URL');
      }

      // Prepare ticket data (following Legal template pattern)
      const ticketData = {
        organizationId,
        customerName: formData.customerName,
        email: formData.email,
        category: formData.category,
        priority: formData.priority,
        subject: formData.subject,
        description: formData.description,
        attachments: attachments.map(file => file.name) // Store file names for now
      };
      console.log('Sending ticket data:', ticketData);

      // Submit ticket to API (using support tickets endpoint)
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/api/v1/support/tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ticketData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit ticket');
      }

      const result = await response.json();
      setTicketId(result.ticketId);
      
      // Extract org prefix from current URL to preserve it in redirect
      const orgRedirectMatch = currentPath.match(/^(\/org_[^\/]+)/);
      const orgPrefix = orgRedirectMatch ? orgRedirectMatch[1] : '';
      
      // Redirect to waiting room with proper org prefix
      const waitingRoomUrl = `${orgPrefix}/templates/support/waiting/${result.ticketId}`;
      console.log('Redirecting to waiting room:', waitingRoomUrl);
      window.location.href = waitingRoomUrl;
      
      if (onTicketSubmitted) {
        onTicketSubmitted(result.ticketId);
      }
    } catch (err) {
      console.error('Error submitting ticket:', err);
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  if (ticketId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--brand-background)' }}>
        <BrandedCard className="max-w-md w-full">
          <div className="text-center">
            <CheckCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--brand-success)' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
              Ticket Submitted Successfully!
            </h2>
            <p className="mb-4" style={{ color: 'var(--brand-text-secondary)' }}>
              Your ticket has been received and our support team will respond shortly.
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--brand-text-secondary)' }}>
                Your Ticket Number:
              </p>
              <p className="text-2xl font-mono font-bold" style={{ color: 'var(--brand-primary)' }}>
                #{ticketId}
              </p>
            </div>
            <p className="text-sm mb-6" style={{ color: 'var(--brand-text-secondary)' }}>
              We've sent a confirmation email to <strong>{formData.email}</strong> with your ticket details.
            </p>
            <div className="space-y-2">
              <BrandedButton
                variant="primary"
                onClick={() => {
                  setTicketId(null);
                  setFormData({
                    customerName: '',
                    email: '',
                    category: 'general',
                    priority: 'medium',
                    subject: '',
                    description: ''
                  });
                  setAttachments([]);
                }}
                className="w-full"
              >
                Submit Another Ticket
              </BrandedButton>
              <BrandedButton
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                Return to Home
              </BrandedButton>
            </div>
          </div>
        </BrandedCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'var(--brand-border)' }}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <BrandedHeader 
            showLogo={true} 
            showTagline={true}
            customTagline="How can we help you today?"
          />
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <BrandedCard className="mb-6">
          <div className="flex items-center mb-6">
            <Headphones size={24} className="mr-3" style={{ color: 'var(--brand-primary)' }} />
            <h2 className="text-xl font-semibold" style={{ color: 'var(--brand-text-primary)' }}>
              Submit a Support Ticket
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BrandedInput
                label="Your Name"
                placeholder="John Doe"
                value={formData.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                required
              />
              <BrandedInput
                label="Email Address"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                Issue Category
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {categoryOptions.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleInputChange('category', value)}
                    className={`p-3 rounded-lg border transition-all ${
                      formData.category === value 
                        ? 'border-2' 
                        : 'border hover:border-gray-400'
                    }`}
                    style={{
                      borderColor: formData.category === value ? 'var(--brand-primary)' : 'var(--brand-border)',
                      backgroundColor: formData.category === value ? 'var(--brand-surface)' : 'transparent'
                    }}
                  >
                    <Icon size={20} className="mx-auto mb-1" style={{ 
                      color: formData.category === value ? 'var(--brand-primary)' : 'var(--brand-text-secondary)' 
                    }} />
                    <p className="text-xs" style={{ 
                      color: formData.category === value ? 'var(--brand-primary)' : 'var(--brand-text-secondary)' 
                    }}>
                      {label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Priority Selection */}
            <BrandedSelect
              label="Priority Level"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value as TicketPriority)}
              options={priorityOptions}
            />

            {/* Issue Details */}
            <BrandedInput
              label="Subject"
              placeholder="Brief description of your issue"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              required
            />

            <BrandedTextarea
              label="Description"
              placeholder="Please provide as much detail as possible about your issue..."
              rows={6}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              required
            />

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                Attachments (optional)
              </label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center" style={{ borderColor: 'var(--brand-border)' }}>
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto mb-2" style={{ color: 'var(--brand-text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--brand-text-muted)' }}>
                    Images, PDFs, or documents up to 10MB
                  </p>
                </label>
              </div>
              
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <span className="text-sm truncate" style={{ color: 'var(--brand-text-primary)' }}>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <BrandedAlert variant="error">
                {error}
              </BrandedAlert>
            )}

            {/* Submit Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BrandedBadge variant={getPriorityColor(formData.priority as TicketPriority)}>
                  {formData.priority.toUpperCase()} Priority
                </BrandedBadge>
                <span className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                  Estimated response: {
                    formData.priority === 'urgent' ? '< 1 hour' :
                    formData.priority === 'high' ? '2-4 hours' :
                    formData.priority === 'medium' ? '24 hours' :
                    '2-3 days'
                  }
                </span>
              </div>
              
              <BrandedButton
                type="submit"
                disabled={isSubmitting}
                className="min-w-[150px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket'
                )}
              </BrandedButton>
            </div>
          </form>
        </BrandedCard>

        {/* Help Section */}
        <BrandedCard>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--brand-text-primary)' }}>
            Need immediate help?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <FileText size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--brand-text-primary)' }}>Knowledge Base</p>
              <p className="text-xs mt-1" style={{ color: 'var(--brand-text-secondary)' }}>
                Browse helpful articles
              </p>
            </div>
            <div className="text-center">
              <MessageSquare size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--brand-text-primary)' }}>Live Chat</p>
              <p className="text-xs mt-1" style={{ color: 'var(--brand-text-secondary)' }}>
                Chat with support agent
              </p>
            </div>
            <div className="text-center">
              <Headphones size={24} className="mx-auto mb-2" style={{ color: 'var(--brand-primary)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--brand-text-primary)' }}>Call Support</p>
              <p className="text-xs mt-1" style={{ color: 'var(--brand-text-secondary)' }}>
                1-800-SUPPORT
              </p>
            </div>
          </div>
        </BrandedCard>
      </main>
    </div>
  );
};